import React,{useContext,useState,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import {AppContext} from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiShield,FiZap,FiUser,FiMapPin,FiClock,FiCalendar,FiX,FiInfo,FiAward,FiDribbble,FiStar}=FiIcons;

const stripHtml=(html)=> html ? html.replace(/<[^>]*>?/gm,'') : '';

const Countdown=({targetDate,marginTop})=> {
  const [timeLeft,setTimeLeft]=useState({d: 0,h: 0,m: 0,s: 0});

  useEffect(()=> {
    const timer=setInterval(()=> {
      const now=new Date().getTime();
      const distance=new Date(targetDate).getTime() - now;
      if (distance < 0) {
        clearInterval(timer);
        return;
      } 
      setTimeLeft({
        d: Math.floor(distance / (1000 * 60 * 60 * 24)),
        h: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        m: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        s: Math.floor((distance % (1000 * 60)) / 1000)
      });
    },1000);
    return ()=> clearInterval(timer);
  },[targetDate]);

  return (
    <motion.div 
      initial={{opacity: 0,y: 20}} 
      animate={{opacity: 1,y: 0}} 
      className="flex gap-4 md:gap-8 text-white select-none pointer-events-none" 
      style={{marginTop: `${marginTop !==undefined ? marginTop : 2}rem`}}
    >
      {[ 
        {v: timeLeft.d,l: 'GIORNI'},
        {v: timeLeft.h,l: 'ORE'},
        {v: timeLeft.m,l: 'MIN'},
        {v: timeLeft.s,l: 'SEC'} 
      ].map((t,i)=> (
        <div key={i} className="flex flex-col items-center">
          <span className="font-league text-6xl md:text-8xl leading-[0.85] tracking-wide drop-shadow-md">{String(t.v).padStart(2,'0')}</span>
          <span className="font-body text-[10px] md:text-xs tracking-[0.4em] text-white/40 mt-2 font-bold uppercase">{t.l}</span>
        </div>
      ))}
    </motion.div>
  );
};

const Home=()=> {
  const {
    teams,getTotalScores,RenderName,textStyles,badgeText,schedule,
    showCountdown,showBadge,eventDate,badgeGap,scoreboardGap,gapBadgeTitle,
    gapScoresRoster,gapTitleTeams,gapRosterEvents,gapPartialsTitleList,
    gapScoreTitleBoxes,gapBoxesPartials,gapEventsTitleGrid,gapDayTitleEvents,
    gapEventsList,gapTeamHeaderLine,gapTeamLineCaptain,gapTeamCaptainGrid,
    gapHeroTitleCountdown,formatDateDDMMYYYY
  }=useContext(AppContext);

  const [selectedEvent,setSelectedEvent]=useState(null);
  const scores=getTotalScores();
  const days=Object.keys(schedule).sort((a,b)=> Number(a) - Number(b));

  // Estraiamo e ordiniamo gli eventi che contribuiscono al punteggio
  const scoringEvents=Object.values(schedule)
    .flat()
    .filter(act=> act.counts_towards_score !==false)
    .sort((a,b)=> {
      if (a.day_number !==b.day_number) return a.day_number - b.day_number;
      return (a.time || '').localeCompare(b.time || '');
    });

  return (
    <div className="pb-20 overflow-x-hidden">
      {/* 1. HERO SECTION */}
      <header className="relative flex flex-col items-center max-w-full px-4 text-center transition-all duration-500" style={{paddingTop: `${badgeGap !==undefined ? badgeGap : 2.5}rem`}}>
        {showBadge && (
          <motion.div initial={{opacity: 0,scale: 0.8}} animate={{opacity: 1,scale: 1}} className="inline-flex items-center gap-4 px-10 py-3 bg-nba-dark border-2 border-nba-border text-white font-league text-lg tracking-[0.6em] shadow-[0_0_40px_rgba(0,0,0,0.5)]">
            <div className="w-2.5 h-2.5 bg-nba-red rounded-full animate-ping"></div>
            {badgeText}
          </motion.div>
        )}
        <div className="w-full max-w-6xl" style={{marginTop: `${gapBadgeTitle !==undefined ? gapBadgeTitle : 4}rem`}}>
          <h1 className="font-league tracking-tighter uppercase italic drop-shadow-[0_10px_30px_rgba(0,0,0,0.8)] dyn-eventTitle leading-[0.85] pr-2">
            {textStyles.eventTitle?.text}
          </h1>
        </div>
        {showCountdown && <Countdown targetDate={eventDate} marginTop={gapHeroTitleCountdown} />}
      </header>

      {/* 2. SCOREBOARD SECTION */}
      <section className="px-4 relative transition-all duration-500" style={{marginTop: `${scoreboardGap !==undefined ? scoreboardGap : 8}rem`}}>
        <div className="w-full text-center">
          <h3 className="font-league uppercase tracking-[0.5em] italic dyn-scoreboardTitle leading-relaxed pr-2">
            {textStyles.scoreboardTitle?.text}
          </h3>
        </div>
        <div className="w-full max-w-6xl mx-auto flex flex-col md:flex-row items-stretch justify-between relative gap-2 md:gap-0" style={{marginTop: `${gapScoreTitleBoxes !==undefined ? gapScoreTitleBoxes : 4}rem`}}>
          {/* Team Alpha Score */}
          <motion.div whileHover={{scale: 1.02}} className="w-full md:w-[49.5%] nba-gradient-alpha p-10 md:p-16 flex flex-col items-center justify-center relative clip-path-nba-l shadow-2xl overflow-hidden group select-none cursor-default">
            <h2 className="font-league text-5xl md:text-8xl text-white tracking-tighter leading-none mb-4 text-center relative z-10 drop-shadow-lg pointer-events-none"><RenderName name={teams[0]?.name} /></h2>
            <div className="font-league text-[13rem] md:text-[18rem] leading-none text-white drop-shadow-[0_15px_25px_rgba(0,0,0,0.7)] relative z-10 pointer-events-none">{scores.t1}</div>
          </motion.div>

          {/* VS Badge */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-40 hidden md:flex select-none pointer-events-none">
            <div className="bg-white text-nba-dark w-24 h-24 md:w-32 md:h-32 flex items-center justify-center font-league text-6xl md:text-8xl border-8 border-nba-dark shadow-[0_0_50px_rgba(0,0,0,0.5)] pt-2 md:pt-4">VS</div>
          </div>

          {/* Team Omega Score */}
          <motion.div whileHover={{scale: 1.02}} className="w-full md:w-[49.5%] nba-gradient-omega p-10 md:p-16 flex flex-col items-center justify-center relative clip-path-nba-r shadow-2xl overflow-hidden group select-none cursor-default">
            <h2 className="font-league text-5xl md:text-8xl text-white tracking-tighter leading-none mb-4 text-center relative z-10 drop-shadow-lg pointer-events-none"><RenderName name={teams[1]?.name} /></h2>
            <div className="font-league text-[13rem] md:text-[18rem] leading-none text-white drop-shadow-[0_15px_25px_rgba(0,0,0,0.7)] relative z-10 pointer-events-none">{scores.t2}</div>
          </motion.div>
        </div>

        {/* PARZIALI MATCH SECTION */}
        {scoringEvents.length > 0 && (
          <div className="w-full max-w-4xl mx-auto" style={{marginTop: `${gapBoxesPartials !==undefined ? gapBoxesPartials : 2}rem`}}>
            <div className="w-full" style={{marginBottom: `${gapPartialsTitleList !==undefined ? gapPartialsTitleList : 1.5}rem`}}>
              <h4 className="font-league uppercase tracking-[0.4em] italic dyn-partialsTitle leading-relaxed pr-2">
                {textStyles.partialsTitle?.text}
              </h4>
            </div>
            
            <div className="bg-nba-card border border-white/10 shadow-2xl divide-y divide-white/5">
              {scoringEvents.map(act=> (
                <div key={act.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:px-8 hover:bg-white/5 transition-colors group gap-6 sm:gap-0">
                  <div className="flex flex-col text-left">
                    <span className="font-league text-2xl sm:text-3xl uppercase italic text-white group-hover:text-white/80 transition-colors leading-none tracking-wide">{act.title}</span>
                    <span className="text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] text-white/40 mt-1">
                      Giorno {act.day_number} {act.time ? <><span className="mx-1 text-white/20">•</span> {act.time}</> : ''}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-center gap-4 sm:gap-6 bg-black/20 px-4 py-3 rounded border border-white/5 relative mt-2 sm:mt-0">
                    {act.status==='live' ? (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2 py-0.5 bg-nba-card border border-red-500/50 rounded-sm text-red-500 text-[9px] font-black tracking-[0.2em] uppercase shadow-[0_0_10px_rgba(239,68,68,0.2)] whitespace-nowrap">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse shadow-[0_0_5px_rgba(239,68,68,0.8)]"></span> LIVE
                      </div>
                    ) : act.status==='final' ? (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-nba-card border border-white/10 rounded-sm text-white/30 text-[9px] font-black tracking-[0.2em] uppercase whitespace-nowrap">
                        FINAL
                      </div>
                    ) : (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-nba-card border border-white/10 rounded-sm text-white/30 text-[9px] font-black tracking-[0.2em] uppercase whitespace-nowrap">
                        IN ARRIVO
                      </div>
                    )}
                    
                    <div className="flex flex-col items-end select-none cursor-default">
                      <span className="text-[9px] font-sans font-bold text-white/40 not-italic uppercase truncate max-w-[60px] sm:max-w-[80px] leading-none mb-1 pointer-events-none" title={stripHtml(teams[0]?.name)}>
                        {stripHtml(teams[0]?.name) || 'TEAM 1'}
                      </span>
                      <span className="font-league text-4xl sm:text-5xl text-nba-red leading-none drop-shadow-md pointer-events-none">{act.scoreT1}</span>
                    </div>
                    
                    <span className="font-league text-2xl text-white/10 mt-3 select-none pointer-events-none">-</span>
                    
                    <div className="flex flex-col items-start select-none cursor-default">
                      <span className="text-[9px] font-sans font-bold text-white/40 not-italic uppercase truncate max-w-[60px] sm:max-w-[80px] leading-none mb-1 pointer-events-none" title={stripHtml(teams[1]?.name)}>
                        {stripHtml(teams[1]?.name) || 'TEAM 2'}
                      </span>
                      <span className="font-league text-4xl sm:text-5xl text-nba-blue leading-none drop-shadow-md pointer-events-none">{act.scoreT2}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* 3. THE ATHLETES - ROSTER SECTION */}
      <section className="max-w-7xl mx-auto px-4 transition-all duration-500" style={{marginTop: `${gapScoresRoster !==undefined ? gapScoresRoster : 8}rem`}}>
        <div className="w-full text-center">
          <h3 className="font-league italic tracking-tighter uppercase dyn-athletesTitle leading-none pr-2">
            {textStyles.athletesTitle?.text}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 lg:gap-24 transition-all duration-500" style={{marginTop: `${gapTitleTeams !==undefined ? gapTitleTeams : 5}rem`}}>
          {teams.map((team,tIdx)=> {
            const isAlpha=tIdx===0;
            const captain=team.members?.[team.captain_index];
            
            // Ordiniamo anche qui in modo che il Capitano Basket sia il primo
            const otherMembers=(team.members || [])
              .filter((_,i)=> i !==team.captain_index)
              .sort((a,b)=> {
                if (a.is_basket_captain && !b.is_basket_captain) return -1;
                if (!a.is_basket_captain && b.is_basket_captain) return 1;
                return 0;
              });

            return (
              <div key={team.id} className="relative flex flex-col">
                {/* Team Header */}
                <div className={`flex items-center gap-6 border-b-4 relative z-10 ${isAlpha ? 'border-nba-red' : 'border-nba-blue'}`} style={{paddingBottom: `${gapTeamHeaderLine !==undefined ? gapTeamHeaderLine : 1.5}rem`}}>
                  <SafeIcon icon={FiShield} className={`text-6xl ${isAlpha ? 'text-nba-red' : 'text-nba-blue'}`} />
                  <span className="font-league text-6xl uppercase italic tracking-tighter pr-2"><RenderName name={team.name} /></span>
                </div>

                {/* Team Captain */}
                {captain && (
                  <div className="relative group z-10" style={{marginTop: `${gapTeamLineCaptain !==undefined ? gapTeamLineCaptain : 2.5}rem`}}>
                    <div className="relative bg-nba-card border border-white/10 p-6 flex items-center gap-8 shadow-2xl">
                      <div className={`w-28 h-28 shrink-0 border-4 ${isAlpha ? 'border-nba-red' : 'border-nba-blue'} bg-nba-dark overflow-hidden`}>
                        {captain.image ? <img src={captain.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" alt={captain.name} /> : <div className="w-full h-full flex items-center justify-center opacity-20"><SafeIcon icon={FiAward} className="text-5xl" /></div>}
                      </div>
                      <div>
                        {/* RUOLO PULITO */}
                        <div className={`text-[10px] sm:text-xs font-black uppercase tracking-[0.3em] mb-2 flex items-center gap-2 ${isAlpha ? 'text-nba-red' : 'text-nba-blue'}`}>
                          <SafeIcon icon={FiZap} className="shrink-0" /> {captain.role || 'Capitano'}
                        </div>
                        {/* NOME PULITO */}
                        <div className="font-league text-5xl text-white uppercase italic tracking-tighter leading-none mb-3 pr-2">
                          {!!captain.number && <span className="text-white/40 mr-2">#{captain.number}</span>}
                          {captain.name}
                        </div>
                        {/* BADGES AREA SOTTO AL NOME */}
                        <div className="flex items-center flex-wrap gap-2">
                          <div className="flex items-center gap-1.5 px-2.5 py-0.5 bg-black/40 border border-yellow-500/50 text-yellow-500 text-lg sm:text-xl not-italic font-league tracking-widest shadow-[0_0_15px_rgba(234,179,8,0.2)] rounded shrink-0" title="Designated Team Captain">
                            <SafeIcon icon={FiStar} className="text-base sm:text-lg pb-0.5" /> CAPTAIN
                          </div>
                          {captain.is_basket_captain && (
                            <div className="flex items-center gap-1.5 px-2.5 py-0.5 bg-black/40 border border-orange-500/50 text-orange-500 text-lg sm:text-xl not-italic font-league tracking-widest shadow-[0_0_15px_rgba(249,115,22,0.2)] rounded shrink-0" title="Basket Captain">
                              <SafeIcon icon={FiDribbble} className="text-base sm:text-lg pb-0.5" /> BASKET
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Other Members Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 relative z-10" style={{marginTop: `${gapTeamCaptainGrid !==undefined ? gapTeamCaptainGrid : 2.5}rem`}}>
                  {otherMembers.map((m,mIdx)=> (
                    <div key={mIdx} className="bg-white/5 border border-white/5 p-4 flex flex-col items-center text-center group hover:bg-white/10 transition-colors overflow-hidden relative">
                      <div className={`w-20 h-20 bg-nba-dark border mb-4 overflow-hidden rounded-full ${m.is_basket_captain ? 'border-orange-500/50' : 'border-white/10'}`}>
                        {m.image ? <img src={m.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300" alt={m.name} /> : <div className="w-full h-full flex items-center justify-center opacity-10"><SafeIcon icon={FiUser} className="text-3xl" /></div>}
                      </div>
                      <div className="flex flex-col items-center justify-center w-full px-1 overflow-hidden">
                        <span className="font-league text-2xl text-white/80 group-hover:text-white uppercase italic tracking-tight truncate w-full text-center pr-2">
                          {!!m.number && <span className="text-white/40 mr-1">#{m.number}</span>}
                          {m.name}
                        </span>
                        <div className="flex flex-col items-center gap-1 mt-1">
                          <span className="text-[10px] font-black uppercase tracking-widest text-white/20 group-hover:text-white/40">
                            {m.role || 'Active Athlete'}
                          </span>
                          {m.is_basket_captain && (
                            <span className="text-[9px] font-black uppercase tracking-widest text-orange-500 bg-orange-500/10 border border-orange-500/20 px-1.5 py-0.5 rounded-sm flex items-center gap-1">
                              <SafeIcon icon={FiDribbble} /> BASKET
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 4. HIGHLIGHTS & MATCHES SECTION */}
      <section className="max-w-7xl mx-auto px-4 pb-20 transition-all duration-500" style={{marginTop: `${gapRosterEvents !==undefined ? gapRosterEvents : 8}rem`}}>
        <div className="w-full">
          <h3 className="font-league italic tracking-tighter uppercase border-b-2 border-white pb-8 dyn-homeEventsTitle leading-none pr-2">
            {textStyles.homeEventsTitle?.text}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" style={{marginTop: `${gapEventsTitleGrid !==undefined ? gapEventsTitleGrid : 4}rem`}}>
          {days.map(dayNum=> {
            const isOddDay=Number(dayNum) % 2 !==0;
            const dayColorBg=isOddDay ? 'bg-nba-red' : 'bg-nba-blue';
            const dayColorTextHover=isOddDay ? 'group-hover:text-nba-red' : 'group-hover:text-nba-blue';
            const dayBorderHover=isOddDay ? 'hover:border-nba-red' : 'hover:border-nba-blue';
            const iconColorClass=isOddDay ? 'text-nba-red' : 'text-nba-blue';

            return (
              <div key={dayNum} className="flex flex-col">
                <div className="bg-white text-nba-dark px-8 py-3 font-league text-4xl italic inline-block skew-x-[-12deg] shadow-xl pr-10 self-start">DAY {dayNum}</div>
                
                <div className="flex flex-col" style={{gap: `${gapEventsList !==undefined ? gapEventsList : 1}rem`,marginTop: `${gapDayTitleEvents !==undefined ? gapDayTitleEvents : 2}rem`}}>
                  {schedule[dayNum].map(act=> (
                    <button key={act.id} onClick={()=> setSelectedEvent(act)} className={`w-full flex flex-col p-6 bg-nba-card border border-white/5 ${dayBorderHover} transition-all group text-left shadow-2xl relative overflow-hidden min-h-[160px]`}>
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${dayColorBg}`}></div>
                      
                      {/* TOP ROW: Date,Time & Status */}
                      <div className="flex justify-between items-start w-full relative z-10 mb-3">
                        <div className="flex flex-col gap-1.5 text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">
                          <span className="flex items-center gap-1.5"><SafeIcon icon={FiCalendar} className={`${iconColorClass} shrink-0`} /> {formatDateDDMMYYYY(act.event_date)}</span>
                          <span className="flex items-center gap-1.5"><SafeIcon icon={FiClock} className={`${iconColorClass} shrink-0`} /> {act.time}</span>
                        </div>
                        <div className="text-right">
                          {act.counts_towards_score ? (
                            act.status==='live' ? (
                              <div className="flex items-center gap-1.5 bg-red-500/10 px-2 py-1 rounded border border-red-500/20">
                                <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse shadow-[0_0_5px_rgba(239,68,68,0.8)]"></span>
                                <span className="text-[9px] text-red-500 font-black tracking-[0.2em] uppercase">LIVE</span>
                              </div>
                            ) : act.status==='final' ? (
                              <div className="text-[9px] text-white/30 font-black tracking-[0.2em] uppercase bg-white/5 px-2 py-1 rounded">FINAL</div>
                            ) : (
                              <div className="text-[9px] text-white/30 font-black tracking-[0.2em] uppercase bg-white/5 px-2 py-1 rounded">IN ARRIVO</div>
                            )
                          ) : act.status==='final' ? (
                            <div className="text-[9px] text-white/30 font-black tracking-[0.2em] uppercase bg-white/5 px-2 py-1 rounded">COMPLETATO</div>
                          ) : null}
                        </div>
                      </div>

                      {/* MIDDLE ROW: Title */}
                      <div className="relative z-10 w-full mb-4 flex-grow">
                        <h4 className={`font-league text-2xl sm:text-3xl text-white ${dayColorTextHover} uppercase transition-colors leading-[1.1] tracking-tight group-hover:drop-shadow-md`}>
                          {act.title}
                        </h4>
                      </div>

                      {/* BOTTOM ROW: Score & Icon */}
                      <div className="flex justify-between items-end w-full relative z-10 mt-auto pt-4 border-t border-white/5">
                        <div>
                          {act.counts_towards_score ? (
                            <div className="flex items-center gap-3 bg-black/20 px-3 py-1.5 rounded border border-white/5">
                              <div className="flex flex-col items-end select-none cursor-default">
                                <span className="text-[9px] font-sans font-bold text-white/40 not-italic uppercase truncate max-w-[70px] leading-none mb-1 pointer-events-none" title={stripHtml(teams[0]?.name)}>
                                  {stripHtml(teams[0]?.name) || 'TEAM 1'}
                                </span>
                                <span className="font-league text-3xl italic text-nba-red leading-none drop-shadow-sm pointer-events-none">{act.scoreT1}</span>
                              </div>
                              <span className="text-white/10 text-xl font-league mt-3 select-none pointer-events-none">-</span>
                              <div className="flex flex-col items-start select-none cursor-default">
                                <span className="text-[9px] font-sans font-bold text-white/40 not-italic uppercase truncate max-w-[70px] leading-none mb-1 pointer-events-none" title={stripHtml(teams[1]?.name)}>
                                  {stripHtml(teams[1]?.name) || 'TEAM 2'}
                                </span>
                                <span className="font-league text-3xl italic text-nba-blue leading-none drop-shadow-sm pointer-events-none">{act.scoreT2}</span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-[9px] font-black bg-white/5 text-white/30 px-2 py-1 rounded uppercase tracking-[0.2em] select-none pointer-events-none">No Score</span>
                          )}
                        </div>
                        <div className={`w-8 h-8 rounded-full bg-white/5 flex items-center justify-center ${dayBorderHover} border border-transparent transition-all`}>
                          <SafeIcon icon={FiInfo} className={`text-white/40 ${dayColorTextHover} transition-colors text-lg`} />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* EVENT MODAL */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-md">
            <motion.div initial={{scale: 0.9,y: 20}} animate={{scale: 1,y: 0}} className="bg-nba-card border border-white/10 w-full max-w-lg p-8 relative shadow-2xl">
              <button onClick={()=> setSelectedEvent(null)} className="absolute top-4 right-4 text-white/40 hover:text-white p-2"><SafeIcon icon={FiX} className="text-2xl" /></button>
              
              <div className={`${Number(selectedEvent.day_number) % 2 !==0 ? 'text-nba-red' : 'text-nba-blue'} font-league text-sm tracking-[0.3em] mb-2 uppercase`}>Giorno {selectedEvent.day_number}</div>
              <h2 className="font-league text-6xl text-white uppercase italic leading-none mb-8 pr-12">{selectedEvent.title}</h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-center gap-4 text-white/80 font-league text-2xl"><SafeIcon icon={FiCalendar} className={`${Number(selectedEvent.day_number) % 2 !==0 ? 'text-nba-red' : 'text-nba-blue'} text-xl`} /> {formatDateDDMMYYYY(selectedEvent.event_date)}</div>
                <div className="flex items-center gap-4 text-white/80 font-league text-2xl"><SafeIcon icon={FiClock} className={`${Number(selectedEvent.day_number) % 2 !==0 ? 'text-nba-red' : 'text-nba-blue'} text-xl`} /> {selectedEvent.time}</div>
                {selectedEvent.address && (<div className="flex items-start gap-4 text-white/80 font-league text-2xl"><SafeIcon icon={FiMapPin} className={`${Number(selectedEvent.day_number) % 2 !==0 ? 'text-nba-red' : 'text-nba-blue'} text-xl mt-1.5`} /><span className="leading-tight">{selectedEvent.address}</span></div>)}
              </div>

              {selectedEvent.notes && (<div className={`bg-white/5 p-6 border-l-4 ${Number(selectedEvent.day_number) % 2 !==0 ? 'border-nba-red' : 'border-nba-blue'} text-white/60 italic text-lg`}>{selectedEvent.notes}</div>)}
              
              {!selectedEvent.counts_towards_score && (
                <div className="mt-6 text-[10px] font-black uppercase tracking-widest text-white/30 text-right">
                  Questo evento non concorre al punteggio
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Home;